import { getNetworkStatus } from '@/lib/network-status';
import {
  getCallDiagnostics,
  getCallDiagnosticsSync,
  getNetworkDiagnosticsOnError,
} from '@/services/logger/call-diagnostics';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import { CancelReason, VonageError } from '@vonage/client-sdk';
import { useCallback, useEffect, useMemo } from 'react';
import {
  ensureSessionReady,
  markCallStarting,
  markVonageCallStart,
  unmarkCallStarting,
  useVonageClient,
} from '../../vonage-client-provider';
import { isNetworkFetchError, isTerminalCallStateError, serializeError } from '../error-utils';
import {
  type VoiceContact,
  trackDeviceCallFailure,
  useVoiceCallState,
} from '../voice-call-container';
import { withRetry } from '../with-retry';

const log = createLogger('IncomingCall', ['calls']);

// NOTE: for these errors, we do not refresh session,
//       instead fetch latest call state and reset local call state
const knownErrorsForRejectCall = ['voice:error:no_invite_found', 'voice:error:invalid_call_action'];
const knownErrorsForAnswerCall = [
  ...knownErrorsForRejectCall,
  'conversation:error:not-allowed',
  'voice:error:inbound_call_failed',
];

/**
 * @internal
 * This hook is used internally by VoiceCallActionsProvider.
 * Consumers should use useVoiceCallActions() instead.
 */
export const useIncomingCall = () => {
  const [call, callDispatch] = useVoiceCallState();
  const vonageClient = useVonageClient();

  useEffect(() => {
    // NOTE: name is actually the phone number without +
    const onCallInvite = vonageClient.on('callInvite', (callId, name, channel) => {
      const phoneNumber = `+${name}`;
      const diagnostics = getCallDiagnosticsSync();
      log.warn('SDK call invite received', {
        callId,
        name,
        channel,
        timestamp: Date.now(),
        diagnostics,
      });

      const contact = { name: phoneNumber, phoneNumber } satisfies VoiceContact;

      tracingService.trackEvent(CallEventName.CallReceived, CallEventLevel.Info, {
        deviceLegId: callId,
        direction: 'inbound',
      });
      callDispatch({ type: 'call:invite', callId, from: { name, channel }, contact });
    });

    const onCallInviteCancel = vonageClient.on('callInviteCancel', (callId, reason) => {
      if (call.hasSecondIncomingCall === false && callId !== call.callId) {
        return;
      }

      if (call.hasSecondIncomingCall === true && call.secondCall?.id !== callId) {
        return;
      }

      const actionType = call.hasSecondIncomingCall ? 'call:second:cancel' : 'call:invite:cancel';

      callDispatch({ type: actionType, reason });
    });

    return () => {
      vonageClient.off('callInvite', onCallInvite);
      vonageClient.off('callInviteCancel', onCallInviteCancel);
    };
  }, [call.callId, call.hasSecondIncomingCall, call.secondCall?.id, callDispatch, vonageClient]);

  const isRinging = useMemo(() => {
    return call.callState === 'ringing' && call.direction === 'incoming';
  }, [call.callState, call.direction]);

  const invite = useMemo(() => {
    return {
      from: call.from?.name,
      channel: call.from?.channel,
    };
  }, [call.from?.name, call.from?.channel]);

  const answerCall = useCallback(async (): Promise<boolean> => {
    const callId = call.callId;
    const callState = call.callState;
    const direction = call.direction;
    const diagnostics = await getCallDiagnostics();

    log.info('Answer attempt', {
      callId,
      callState,
      direction,
      hasCallId: !!callId,
      timestamp: Date.now(),
      diagnostics,
    });

    tracingService.trackEvent(CallEventName.AttemptAnswer, CallEventLevel.Info, {
      deviceLegId: callId ?? undefined,
      direction: 'inbound',
    });

    if (!callId) {
      log.error('Answer without callId', {
        callState,
        direction,
        timestamp: Date.now(),
        diagnostics,
      });
      return false;
    }

    let success = true;

    const handleAnswerCallFailure = async (error: unknown) => {
      // SDK reported no active session — mid-handshake race after a
      // session refresh. Mirror use-server-call's behavior: await any
      // in-flight refresh (capped at 6s) and let withRetry fire the
      // next attempt rather than landing in the same window.
      if (error instanceof VonageError && error.code === 'session:error:no-active-session') {
        log.warn('Answer failed', {
          callId,
          reason: 'session_not_ready_retrying',
          ...serializeError(error),
          timestamp: Date.now(),
          diagnostics,
        });
        await ensureSessionReady();
        return; // let withRetry fire the next attempt
      }

      if (error instanceof VonageError) {
        if (knownErrorsForAnswerCall.includes(error.code ?? '')) {
          try {
            const leg = await vonageClient.getLeg(callId);

            if (leg.status === 'completed') {
              log.info('Answer failed', {
                callId,
                reason: 'leg_completed',
                legStatus: leg.status,
                vonageErrorCode: error.code,
                ...serializeError(error),
                timestamp: Date.now(),
                diagnostics,
              });
              callDispatch({ type: 'call:hangup', reason: CancelReason.AnsweredElsewhere });
              success = false;
              return false;
            }
          } catch (legError) {
            log.info('Answer failed', {
              callId,
              reason: 'terminal_call_state_error',
              ...serializeError(error),
              legError: serializeError(legError),
              timestamp: Date.now(),
              diagnostics,
            });
            callDispatch({ type: 'call:hangup', reason: 'LOCAL_ERROR' });
            success = false;
            return false;
          }
        }
      }

      if (isNetworkFetchError(error)) {
        const failureNetwork = getNetworkStatus();
        log.error('Answer failed', {
          callId,
          reason: 'network_fetch_error',
          ...serializeError(error),
          timestamp: Date.now(),
          diagnostics,
          failureNetworkStatus: failureNetwork,
        });
        getNetworkDiagnosticsOnError()
          .then((enriched) => {
            log.info('Answer failed', {
              callId,
              reason: 'network_fetch_error_enriched',
              ...serializeError(error),
              timestamp: Date.now(),
              enrichedDiagnostics: enriched,
            });
          })
          .catch(() => {});
        callDispatch({ type: 'call:hangup', reason: 'LOCAL_ERROR' });
        success = false;
        return false;
      }

      if (isTerminalCallStateError(error)) {
        log.info('Answer failed', {
          callId,
          reason: 'terminal_call_state_error',
          ...serializeError(error),
          timestamp: Date.now(),
          diagnostics,
        });
        callDispatch({ type: 'call:hangup', reason: 'LOCAL_ERROR' });
        success = false;
        return false;
      }

      // Unknown answer errors do not trigger a session refresh. `withRetry`
      // retries once; if that fails, `onFailure` below both attempts a
      // fail-safe hangup and clears the UI. Sentry already captures the
      // error via `withRetry.sentry`.
      log.info('Answer failed', {
        callId,
        reason: 'unknown_error_retrying',
        ...serializeError(error),
        timestamp: Date.now(),
        diagnostics,
        sessionRefreshAttempted: false,
        sessionRefreshSkipReason: 'unknown_error',
      });
    };

    try {
      await withRetry(
        async () => {
          // Gate the heartbeat while we arm an inbound answer. The active-call
          // marker is driven by `state.callId`, which is already populated here
          // (callId came from the invite), so technically the mirror effect
          // might already cover us. Setting the flag is cheap belt-and-braces
          // in case the reducer hasn't flushed yet, and matches use-server-call.
          markCallStarting();
          try {
            // See use-server-call.ts for the reasoning. In short: await any
            // in-flight session refresh before invoking the SDK so we don't
            // race the 5s `startVerifyResponseTimer` handshake window.
            await ensureSessionReady();
            await vonageClient.answer(callId);
            callDispatch({ type: 'call:invite:answered' });
            // See markVonageCallStart in use-server-call.ts.
            markVonageCallStart();
            log.info('Answer succeeded', { callId, timestamp: Date.now(), diagnostics });

            // Log actual audio device being used after call is answered
            try {
              const peerConn = vonageClient.getPeerConnection(callId);
              const audioSender = peerConn?.getSenders().find((s) => s.track?.kind === 'audio');
              const activeAudioTrack = audioSender?.track ?? null;
              const activeDiagnostics = await getCallDiagnostics({ activeAudioTrack });
              log.info('Active call device info', {
                callId,
                timestamp: Date.now(),
                diagnostics: activeDiagnostics,
              });
            } catch (deviceError) {
              // Don't fail the call if device logging fails
              console.warn('Failed to log active device info:', deviceError);
            }
          } finally {
            unmarkCallStarting();
          }
        },
        {
          name: 'attemptAnswer',
          maxRetries: 1,
          onRetry: handleAnswerCallFailure,
          skipFailureIfHandled: true,
          sentry: {
            tag: 'vonage_accept_call_error',
            extra: {
              callId,
            },
          },
          onFailure: (error: unknown) => {
            success = false;
            log.error('Answer failed', {
              callId,
              reason: 'max_retries_exceeded',
              ...serializeError(error),
              timestamp: Date.now(),
              diagnostics,
            });
            tracingService.trackEvent(CallEventName.VonageOperationError, CallEventLevel.Error, {
              deviceLegId: callId,
              direction: 'inbound',
              errorMessage: error instanceof Error ? error.message : String(error),
            });
            trackDeviceCallFailure(
              call,
              CallEventName.VonageOperationError,
              {
                operation: 'answer',
                reason: 'max_retries_exceeded',
                errorMessage: error instanceof Error ? error.message : String(error),
              },
              { stage: 'call_ringing', callId },
            );
            // NOTE: fail safe hangup, in case mic is still alive, we try to hangup before clearing UI
            vonageClient.hangup(callId).catch((hangupError) => {
              log.info('Answer cleanup hangup failed', {
                callId,
                reason: 'cleanup_hangup_failed',
                ...serializeError(hangupError),
                timestamp: Date.now(),
              });
            });
            callDispatch({ type: 'call:hangup', reason: 'LOCAL_ERROR' });
          },
        },
      );
    } catch (error) {
      success = false;
      log.error('Answer failed', {
        callId,
        reason: 'unexpected_error',
        error: error instanceof Error ? error.message : String(error),
        timestamp: Date.now(),
        diagnostics,
      });
      tracingService.trackEvent(CallEventName.VonageOperationError, CallEventLevel.Error, {
        deviceLegId: callId,
        direction: 'inbound',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      trackDeviceCallFailure(
        call,
        CallEventName.VonageOperationError,
        {
          operation: 'answer',
          reason: 'unexpected_error',
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        { stage: 'call_ringing', callId },
      );
    }

    return success;
  }, [callDispatch, call, vonageClient]);

  const rejectCall = useCallback(async () => {
    const callId = call.callId;
    const diagnostics = await getCallDiagnostics();

    log.info('Reject attempt', {
      callId,
      hasCallId: !!callId,
      timestamp: Date.now(),
      diagnostics,
    });

    if (!callId) {
      log.error('Reject without callId', { timestamp: Date.now(), diagnostics });
      return;
    }

    const handleRejectCallFailure = async (error: unknown) => {
      if (error instanceof VonageError) {
        if (knownErrorsForRejectCall.includes(error.code ?? '')) {
          try {
            const leg = await vonageClient.getLeg(callId);

            if (leg.status === 'completed') {
              log.info('Reject failed', {
                callId,
                reason: 'leg_completed',
                legStatus: leg.status,
                ...serializeError(error),
                timestamp: Date.now(),
                diagnostics,
              });
              callDispatch({ type: 'call:invite:rejected', rejectedCallId: callId });
              return false;
            }
          } catch (legError) {
            log.info('Reject failed', {
              callId,
              reason: 'terminal_call_state_error',
              ...serializeError(error),
              legError: serializeError(legError),
              timestamp: Date.now(),
              diagnostics,
            });
            callDispatch({ type: 'call:invite:rejected', rejectedCallId: callId });
            return false;
          }
        }
      }

      if (isNetworkFetchError(error)) {
        const failureNetwork = getNetworkStatus();
        log.error('Reject failed', {
          callId,
          reason: 'network_fetch_error',
          ...serializeError(error),
          timestamp: Date.now(),
          diagnostics,
          failureNetworkStatus: failureNetwork,
        });
        getNetworkDiagnosticsOnError()
          .then((enriched) => {
            log.info('Reject failed', {
              callId,
              reason: 'network_fetch_error_enriched',
              ...serializeError(error),
              timestamp: Date.now(),
              enrichedDiagnostics: enriched,
            });
          })
          .catch(() => {});
        callDispatch({ type: 'call:invite:rejected', rejectedCallId: callId });
        return false;
      }

      if (isTerminalCallStateError(error)) {
        log.info('Reject failed', {
          callId,
          reason: 'terminal_call_state_error',
          ...serializeError(error),
          timestamp: Date.now(),
          diagnostics,
        });
        callDispatch({ type: 'call:invite:rejected', rejectedCallId: callId });
        return false;
      }

      // Unknown reject errors do not trigger a session refresh. `withRetry`
      // retries once; if that fails, `onFailure` clears the UI and Sentry
      // already captures the error via `withRetry.sentry`.
      log.info('Reject failed', {
        callId,
        reason: 'unknown_error_retrying',
        ...serializeError(error),
        timestamp: Date.now(),
        diagnostics,
        sessionRefreshAttempted: false,
        sessionRefreshSkipReason: 'unknown_error',
      });
    };

    try {
      await withRetry(
        async () => {
          await vonageClient.reject(callId);
          callDispatch({ type: 'call:invite:rejected', rejectedCallId: callId });
        },
        {
          name: 'attemptReject',
          maxRetries: 1,
          onRetry: handleRejectCallFailure,
          skipFailureIfHandled: true,
          sentry: {
            tag: 'vonage_reject_call_error',
            extra: {
              callId,
            },
          },
          onFailure: (error: unknown) => {
            log.error('Reject failed', {
              callId,
              reason: 'max_retries_exceeded',
              ...serializeError(error),
              timestamp: Date.now(),
              diagnostics,
            });
            tracingService.trackEvent(CallEventName.VonageOperationError, CallEventLevel.Error, {
              deviceLegId: callId,
              direction: 'inbound',
              errorMessage: error instanceof Error ? error.message : String(error),
            });
            trackDeviceCallFailure(
              call,
              CallEventName.VonageOperationError,
              {
                operation: 'reject',
                reason: 'max_retries_exceeded',
                errorMessage: error instanceof Error ? error.message : String(error),
              },
              { stage: 'call_ringing', callId },
            );
            callDispatch({ type: 'call:invite:rejected', rejectedCallId: callId });
          },
        },
      );
    } catch (error) {
      log.error('Reject failed', {
        callId,
        reason: 'unexpected_error',
        ...serializeError(error),
        timestamp: Date.now(),
        diagnostics,
      });
      tracingService.trackEvent(CallEventName.VonageOperationError, CallEventLevel.Error, {
        deviceLegId: callId,
        direction: 'inbound',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      trackDeviceCallFailure(
        call,
        CallEventName.VonageOperationError,
        {
          operation: 'reject',
          reason: 'unexpected_error',
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        { stage: 'call_ringing', callId },
      );
    }
  }, [callDispatch, call, vonageClient]);

  return { answerCall, rejectCall, isRinging, invite };
};
